import React from "react";
import Section from "./Section";

export default function HTML_Formatting() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">📝 HTML Text Formatting</h1>

      {/* 🔹 Introduction */}
      <p className="tutorial-intro">
        HTML provides several <strong>formatting elements</strong> that define
        the way text appears — bold, italic, small, highlighted, etc. These tags
        make text more meaningful and readable.
      </p>

      {/* 🔹 List of Common Formatting Tags */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
          borderRadius: "12px",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <h3
          style={{
            color: " #54b6f7ff",
            fontWeight: 700,
            fontSize: "1.5rem",
            marginBottom: "0.5rem",
          }}
        >
          📜 Common HTML Text Formatting Tags
        </h3>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code_1">&lt;b&gt;</code> – Bold text
          </li>
          <li>
            <code className="highlight_code_1">&lt;strong&gt;</code> – Important
            text
          </li>
          <li>
            <code className="highlight_code_1">&lt;i&gt;</code> – Italic text
          </li>
          <li>
            <code className="highlight_code_1">&lt;em&gt;</code> – Emphasized
            text
          </li>
          <li>
            <code className="highlight_code_1">&lt;mark&gt;</code> – Marked
            (highlighted) text
          </li>
          <li>
            <code className="highlight_code_1">&lt;small&gt;</code> – Smaller
            text
          </li>
          <li>
            <code className="highlight_code_1">&lt;del&gt;</code> – Deleted text
          </li>
          <li>
            <code className="highlight_code_1">&lt;ins&gt;</code> – Inserted
            (underlined) text
          </li>
          <li>
            <code className="highlight_code_1">&lt;sub&gt;</code> – Subscript
            text
          </li>
          <li>
            <code className="highlight_code_1">&lt;sup&gt;</code> – Superscript
            text
          </li>
        </ul>
      </div>

      {/* Section 1️⃣ — Bold and Strong */}
      <Section
        title="1️⃣ Bold and Strong Text"
        html={`<p>This is <b>bold</b> text using the <b>&lt;b&gt;</b> tag.</p>
<p>This is <strong>important</strong> text using the <strong>&lt;strong&gt;</strong> tag.</p>`}
        description="The <b>b</b> tag makes text bold for visual emphasis, while <strong>strong</strong> also conveys importance semantically."
      />

      {/* Section 2️⃣ — Italic and Emphasized */}
      <Section
        title="2️⃣ Italic and Emphasized Text"
        html={`<p>This is <i>italic</i> text using the <i>&lt;i&gt;</i> tag.</p>
<p>This is <em>emphasized</em> text using the <em>&lt;em&gt;</em> tag.</p>`}
        description="The <i>i</i> tag italicizes text visually, while <em>em</em> adds emphasis in both style and meaning."
      />

      {/* Section 3️⃣ — Marked, Small, and Deleted */}
      <Section
        title="3️⃣ Marked, Small, and Deleted Text"
        html={`<p>This is <mark>highlighted</mark> text using <mark>&lt;mark&gt;</mark>.</p>
<p>This is <small>smaller</small> text using <small>&lt;small&gt;</small>.</p>
<p>This is <del>deleted</del> text using <del>&lt;del&gt;</del>.</p>`}
        description="Use <mark> to highlight text, <small> for fine print, and <del> for deleted or outdated content."
      />

      {/* Section 4️⃣ — Inserted and Sub/Sup Script */}
      <Section
        title="4️⃣ Inserted, Subscript, and Superscript"
        html={`<p>This is <ins>inserted</ins> text using <ins>&lt;ins&gt;</ins>.</p>
<p>Chemical Formula: H<sub>2</sub>O</p>
<p>Mathematical Power: X<sup>2</sup></p>`}
        description="Use <ins> for inserted text (underlined), <sub> for subscript (below line), and <sup> for superscript (above line)."
      />

      {/* Section 5️⃣ — Quotation and Citation */}
      <Section
        title="5️⃣ Quotation and Citation Tags"
        html={`<p>He said, <q>Practice makes perfect!</q></p>
<p><blockquote cite="https://www.wikipedia.org">
  HTML is the standard markup language for creating web pages.
</blockquote></p>
<p>The book <cite>Introduction to HTML</cite> is a great resource.</p>`}
        description="The <q> tag defines short inline quotes, <blockquote> for longer quotes (indented), and <cite> for citing works or sources."
      />

      {/* Section 6️⃣ — Abbreviation and Address */}
      <Section
        title="6️⃣ Abbreviation and Address"
        html={`<p><abbr title="HyperText Markup Language">HTML</abbr> is a markup language.</p>
<address>
  Written by Sanjana Tiwari<br/>
  Visit us at: www.example.com<br/>
  Contact: info@example.com
</address>`}
        description="The <abbr> tag provides the full form of abbreviations (on hover), and <address> defines contact information."
      />

      {/* Section 7️⃣ — Bi-Directional Override (bdo) */}
      <Section
        title="7️⃣ Bi-Directional Override (BDO)"
        html={`<p>Normal text direction: ABC</p>
<p><bdo dir="rtl">Reversed text direction: ABC</bdo></p>`}
        description="The <bdo> tag overrides the default text direction — useful for right-to-left languages."
      />

      {/* ✅ Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary — HTML Formatting Tags</h2>
        <ul className="tutorial-list-points">
          <li>
            <b>&lt;b&gt;</b> and <strong>&lt;strong&gt;</strong> → Bold text
          </li>
          <li>
            <i>&lt;i&gt;</i> and <em>&lt;em&gt;</em> → Italic text
          </li>
          <li>
            <mark>&lt;mark&gt;</mark> → Highlighted text
          </li>
          <li>
            <del>&lt;del&gt;</del> and <ins>&lt;ins&gt;</ins> → Deleted /
            Inserted text
          </li>
          <li>
            <sub>&lt;sub&gt;</sub> / <sup>&lt;sup&gt;</sup> → Subscript /
            Superscript
          </li>
          <li>
            <q>&lt;q&gt;</q> / <blockquote>&lt;blockquote&gt;</blockquote> →
            Quotes
          </li>
          <li>
            <abbr>&lt;abbr&gt;</abbr> / <address>&lt;address&gt;</address> →
            Abbreviation and contact info
          </li>
          <li>
            <bdo>&lt;bdo&gt;</bdo> → Bi-directional text override
          </li>
        </ul>
      </div>
    </div>
  );
}
